<ng-container *transloco="let t; read: 'adminOperationTemplates'">
  <app-grid-page [title]="t('title')">
    <app-grid
      [columns]="columns()"
      [data]="gridData()"
      [rowId]="rowId"
      [loading]="isLoading() || isFleetLoading() || isVersionsLoading()"
      [page]="page()"
      [pageSize]="size()"
      [totalElements]="totalElements()"
      [totalPages]="totalPages()"
      [sort]="sort()"
      [tabs]="gridTabs()"
      [activeTabId]="activeTab()"
      [searchable]="activeTab() === 'templates'"
      [detailEnabled]="activeTab() === 'templates' || (activeTab() === 'history' && !!selectedVersion())"
      detailMode="split"
      [detailWidth]="620"
      [selectedRow]="activeTab() === 'history' ? selectedVersion() : selectedTemplate()"
      [primaryAction]="activeTab() === 'templates' ? { label: t('addTemplate'), icon: 'icon-content-add24px' } : null"
      (tabChange)="onTabChange($event)"
      (sortChange)="onSortChange($event)"
      (pageChange)="onPageChange($event)"
      (pageSizeChange)="onPageSizeChange($event)"
      (rowClick)="onRowClick($event)"
      (primaryActionClick)="openAddDialog()"
    >
      <!-- ══ Templates columns ══ -->
      <ng-template gridCell="profile" let-row>
        @let pr = asTemplate(row);
        <div class="min-w-0">
          <span class="block truncate text-[14px] font-semibold text-ink-1">{{ pr.operationProfileId }}</span>
          <div class="mt-0.5 truncate text-[12px] text-ink-2">
            {{ t('row.operations', { count: operationsCount(pr) }) }}
          </div>
        </div>
      </ng-template>

      <ng-template gridCell="status" let-row>
        @if (rowFlag(asTemplate(row)); as flag) {
          <span class="flex items-center gap-2 text-[14px] text-ink-1">
            <app-badge [color]="flagDot(flag)" [size]="12" />
            {{ t('flags.' + flag) }}
          </span>
        }
      </ng-template>

      <ng-template gridCell="powerMode" let-row>
        @let p = asTemplate(row);
        <span class="flex items-center gap-2 text-[14px] capitalize text-ink-1">
          <app-badge [color]="powerModeDot(p.powerMode)" [size]="12" />
          {{ p.powerMode.toLowerCase() }}
        </span>
      </ng-template>

      <ng-template gridCell="schedule" let-row>
        <span class="truncate text-[13px] text-ink-2">{{ rowScheduleLabel(asTemplate(row)) }}</span>
      </ng-template>

      <ng-template gridCell="week" let-row>
        <div class="w-[150px]">
          <app-schedule-strip
            [bands]="templateBands(asTemplate(row))"
            [coverageBase]="true"
            [height]="34"
            [hideLabels]="true"
          />
        </div>
      </ng-template>

      <!-- ══ Adoption columns ══ -->
      <ng-template gridCell="aName" let-row>
        <span class="truncate text-[14px] font-semibold text-ink-1">{{ asAdoption(row).operationProfileId }}</span>
      </ng-template>
      <ng-template gridCell="aBar" let-row>
        <div class="w-[300px] py-1">
          <app-stacked-bar [segments]="adoptionSegmentsOf(asAdoption(row))" [height]="12" [labels]="false" [legend]="false" />
        </div>
      </ng-template>
      <ng-template gridCell="aUpToDate" let-row>
        <span class="font-num text-[14px] font-semibold text-success">{{ asAdoption(row).upToDate }}</span>
      </ng-template>
      <ng-template gridCell="aOutdated" let-row>
        @let out = asAdoption(row).outdated;
        <span class="font-num text-[14px] font-semibold" [class.text-warning]="out > 0" [class.text-ink-1]="out === 0">{{ out }}</span>
      </ng-template>
      <ng-template gridCell="aNone" let-row>
        <span class="font-num text-[14px] font-semibold text-ink-2">{{ asAdoption(row).notProvisioned }}</span>
      </ng-template>
      <ng-template gridCell="aVersion" let-row>
        <span class="font-num text-[13px] font-semibold text-ink-1">v{{ asAdoption(row).currentVersion }}</span>
      </ng-template>

      <!-- ══ History columns (accordion: header = latest, children = past) ══ -->
      <ng-template gridCell="hName" let-row>
        @let v = asVersion(row);
        @if (v.isHeader) {
          <button
            type="button"
            (click)="toggleTemplateHistory(v.templateId); $event.stopPropagation()"
            class="flex min-w-0 items-center gap-2 text-left"
          >
            @if (v.siblingCount > 1) {
              <app-icon
                [name]="isTemplateExpanded(v.templateId) ? 'icon-arrow-dropdown' : 'icon-arrow-right'"
                [size]="16"
                class="flex-none text-ink-3"
              />
            } @else {
              <span class="w-4 flex-none"></span>
            }
            <span class="truncate text-[14px] font-semibold text-ink-1">{{ v.templateName }}</span>
            @if (v.siblingCount > 1) {
              <span class="flex-none text-[12px] text-ink-3">{{ t('history.versionCount', { count: v.siblingCount }) }}</span>
            }
          </button>
        }
      </ng-template>
      <ng-template gridCell="hVersion" let-row>
        @let v = asVersion(row);
        <span class="flex items-center gap-2" [class.pl-6]="v.isChild">
          <span class="font-num text-[14px] font-semibold" [class.text-ink-1]="!v.isChild" [class.text-ink-2]="v.isChild">v{{ v.version }}</span>
          @if (v.current) {
            <app-label-badge dot="green">{{ t('history.currentTag') }}</app-label-badge>
          }
        </span>
      </ng-template>
      <ng-template gridCell="hPublished" let-row>
        <span class="text-[13px] text-ink-2">{{ publishedDate(asVersion(row).created) }}</span>
      </ng-template>
      <ng-template gridCell="hOps" let-row>
        <span class="text-[13px] text-ink-2">{{ t('row.operations', { count: asVersion(row).operations.length }) }}</span>
      </ng-template>

      <!-- ══ Detail inspector — Templates (settings/operations) or History (version) ══ -->
      <ng-template gridDetail let-row>
        @if (activeTab() === 'templates' && selectedTemplate(); as def) {
          <div class="@container flex h-full min-h-0 flex-col">
            <div class="flex-none">
              <div class="flex items-center gap-3 border-b border-hairline px-3 py-3 @lg:px-6">
                <app-button
                  variant="secondary"
                  size="sm"
                  iconLeft="icon-edit-pencil"
                  [label]="t('actions.edit')"
                  (activated)="editSelectedTemplate()"
                />
                <app-button
                  variant="primary"
                  size="sm"
                  destructive
                  iconLeft="icon-delete-trash"
                  [label]="t('actions.delete')"
                  (activated)="deleteSelectedTemplate()"
                />
              </div>

              <div class="px-3 pt-4 @lg:px-6">
                <div class="flex items-center justify-between gap-4">
                  <span class="min-w-0 truncate font-display text-[28px] font-bold leading-9 text-ink-1">
                    {{ def.operationProfileId }}
                  </span>
                  <span class="flex flex-none items-center gap-2 text-[14px] capitalize text-ink-1">
                    <app-badge [color]="powerModeDot(def.powerMode)" [size]="12" />
                    {{ def.powerMode.toLowerCase() }}
                  </span>
                </div>
                <div class="mt-1 text-[12px] text-ink-2">
                  {{ t('detail.profileId', { id: def.operationProfileId } ) }} ·
                  {{ t('row.operations', { count: operations().length }) }}
                </div>

                <!-- In-pane detail tabs: Power settings / Operations -->
                <div class="mt-3 flex h-12 items-stretch gap-0.5 overflow-x-auto border-b border-hairline">
                  @for (tab of [
                    { id: 'overview', label: t('tabs.powerSettings') },
                    { id: 'operations', label: t('tabs.operations'), badge: operations().length }
                  ]; track tab.id) {
                    <button
                      type="button"
                      (click)="setTab($any(tab.id))"
                      class="relative flex items-center gap-2 px-3.5"
                      [class.text-ink-1]="detailTab() === tab.id"
                      [class.text-ink-2]="detailTab() !== tab.id"
                      [class.hover:text-ink-1]="detailTab() !== tab.id"
                    >
                      @if (tab.badge !== undefined) {
                        <span
                          class="flex h-5 w-5 flex-none items-center justify-center rounded-pill border border-strong text-[11px] font-bold"
                        >
                          {{ tab.badge }}
                        </span>
                      }
                      <span class="whitespace-nowrap font-display text-[14px] font-bold uppercase tracking-[0.03em]">
                        {{ tab.label }}
                      </span>
                      @if (detailTab() === tab.id) {
                        <div class="absolute inset-x-0 -bottom-px h-1 bg-action"></div>
                      }
                    </button>
                  }
                </div>
              </div>
            </div>

            <div class="min-h-0 flex-1 overflow-auto px-3 py-5 @lg:px-6">
              @if (detailTab() === 'overview') {
                <div class="flex flex-col gap-5">
                  <div class="rounded-card bg-card p-5 shadow-raised">
                    <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
                      <span class="text-[11px] font-bold uppercase tracking-wide text-ink-3">
                        {{ t('powerThresholds') }}
                      </span>
                      <app-label-badge [dot]="def.allowWakeup ? 'green' : null">{{ def.allowWakeup ? t('wakeupAllowed') : t('wakeupBlocked') }}</app-label-badge>
                    </div>
                    <app-power-threshold-bar
                      [shutdownPct]="def.noPowerPercentage"
                      [shutdownOutPct]="def.noPowerPercentageOut"
                      [lowPct]="def.lowPowerPercentage"
                      [lowOutPct]="def.lowPowerPercentageOut"
                    />
                    <div class="mt-3 text-[12px] text-ink-2">
                      {{
                        t('thresholdSummary', {
                          lowIn: def.lowPowerPercentage,
                          lowOut: def.lowPowerPercentageOut,
                          noIn: def.noPowerPercentage,
                          noOut: def.noPowerPercentageOut
                        })
                      }}
                    </div>
                  </div>

                  <div>
                    <div class="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-ink-3">
                      {{ t('defaultValues') }}
                    </div>
                    <div class="grid grid-cols-2 gap-px overflow-hidden rounded-card bg-hairline shadow-raised @xl:grid-cols-2">
                      @for (d of [
                        { label: t('fields.render'), value: formatInterval(def.renderInterval) },
                        { label: t('fields.connection'), value: formatInterval(def.connectionInterval) }
                      ]; track d.label) {
                        <div class="flex flex-col gap-1 bg-card px-4 py-3">
                          <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ d.label }}</span>
                          <span class="font-num text-[15px] font-semibold text-ink-1">{{ d.value }}</span>
                        </div>
                      }
                    </div>
                    <p class="mt-2 text-[12px] text-ink-2">{{ t('inheritNote') }}</p>
                  </div>

                  <div class="rounded-card bg-card p-5 shadow-raised">
                    <div class="mb-3.5 flex items-center justify-between">
                      <span class="text-[11px] font-bold uppercase tracking-wide text-ink-3">
                        {{ t('weeklyCoverage') }}
                      </span>
                    </div>
                    <app-schedule-strip [bands]="coverageBands()" [coverageBase]="true" [height]="130" [axis]="true" />
                    <p class="mt-3.5 text-[12px] text-ink-2">{{ t('coverageNote') }}</p>
                  </div>
                </div>
              } @else if (detailTab() === 'operations') {
                <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <span class="text-[12px] text-ink-2">{{ t('operationsNote') }}</span>
                  <button
                    type="button"
                    (click)="openAddOperationDialog()"
                    class="flex items-center gap-1.5 rounded-control border border-hairline bg-card px-3 py-1.5 text-[12px] font-semibold text-ink-1 hover:bg-sunken"
                  >
                    <app-icon name="icon-content-add24px" [size]="14" />
                    {{ t('actions.addOperation') }}
                  </button>
                </div>

                @if (operations().length === 0) {
                  <div class="flex h-40 items-center justify-center text-sm text-ink-3">
                    {{ t('noOperations') }}
                  </div>
                } @else {
                  <div class="flex flex-col gap-3.5">
                    @for (op of operations(); track op.operationId; let i = $index) {
                      <div class="rounded-card bg-card p-5 shadow-raised">
                        <div class="mb-3 flex items-start justify-between gap-3">
                          <div class="flex min-w-0 items-center gap-2.5">
                            <span
                              class="h-2.5 w-2.5 flex-none rounded-[3px]"
                              [style.background]="operationColor(i)"
                            ></span>
                            <div class="min-w-0">
                              <div class="truncate font-num text-[15px] font-semibold text-ink-1">
                                {{ op.operationId }}
                              </div>
                              <div class="mt-0.5 truncate text-[12px] text-ink-2">
                                {{ operationScheduleLabel(op) }}
                              </div>
                            </div>
                          </div>
                          <div class="flex flex-none items-center gap-1.5">
                            @if (op.sleep) {
                              <app-label-badge dot="blue">{{ t('flags.sleep') }}</app-label-badge>
                            }
                            @if (op.shutdown) {
                              <app-label-badge dot="red">{{ t('flags.shutdown') }}</app-label-badge>
                            }
                            @if (!op.sleep && !op.shutdown) {
                              <app-label-badge dot="green">{{ t('flags.active') }}</app-label-badge>
                            }
                            <app-button
                              variant="secondary"
                              size="sm"
                              iconLeft="icon-edit-pencil"
                              [label]="t('actions.edit')"
                              (activated)="editOperation(op)"
                            />
                            <app-button
                              variant="secondary"
                              size="sm"
                              destructive
                              iconLeft="icon-delete-trash"
                              [label]="t('actions.delete')"
                              (activated)="deleteOperation(op)"
                            />
                          </div>
                        </div>

                        <div class="flex flex-col items-stretch gap-4 @2xl:flex-row">
                          <div class="w-full flex-none @2xl:w-[240px]">
                            <app-schedule-strip
                              [days]="op.days"
                              [startTime]="op.startTime"
                              [endTime]="op.endTime"
                              [sleep]="op.sleep"
                              [shutdown]="op.shutdown"
                              [height]="82"
                            />
                          </div>

                          <div class="flex min-w-0 flex-1 flex-col gap-3">
                            <div class="flex items-center gap-2.5">
                              <span class="min-w-[78px] text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ t('op.battery') }}</span>
                              <div class="flex flex-wrap gap-1.5">
                                @for (s of op.batteryPowerStatusList; track s) {
                                  <app-label-badge [dot]="batteryStatusDot(s)">{{ batteryStatusLabel(s) }}</app-label-badge>
                                }
                              </div>
                            </div>
                            <div class="flex items-center gap-2.5">
                              <span class="min-w-[78px] text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ t('illumination') }}</span>
                              <div class="flex-1"><app-illumination-bar [value]="illuminationOf(op)" /></div>
                            </div>
                            <div class="grid grid-cols-2 gap-px overflow-hidden rounded-card bg-hairline shadow-raised">
                              @for (f of [
                                { label: t('fields.render'), value: formatInterval(op.renderInterval) },
                                { label: t('fields.connection'), value: formatInterval(op.connectionInterval) }
                              ]; track f.label) {
                                <div class="flex flex-col gap-1 bg-card px-3 py-2.5">
                                  <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-3">{{ f.label }}</span>
                                  <span class="font-num text-[14px] font-semibold text-ink-1">{{ f.value }}</span>
                                </div>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                }
              }
            </div>
          </div>
        } @else if (activeTab() === 'history' && selectedVersion(); as ver) {
          <!-- Version inspector: the published snapshot's settings + operations. -->
          <div class="@container flex h-full min-h-0 flex-col">
            <div class="flex-none border-b border-hairline px-3 pb-4 pt-5 @lg:px-6">
              <div class="flex items-center justify-between gap-4">
                <span class="min-w-0 truncate font-display text-[26px] font-bold leading-9 text-ink-1">
                  {{ ver.templateName }} · v{{ ver.version }}
                </span>
                @if (ver.current) {
                  <app-label-badge dot="green">{{ t('history.currentTag') }}</app-label-badge>
                }
              </div>
              <div class="mt-1 text-[12px] text-ink-2">
                {{ t('history.publishedOn', { date: publishedDate(ver.created) }) }}
              </div>
            </div>

            <div class="min-h-0 flex-1 overflow-auto px-3 py-5 @lg:px-6">
              <div class="mb-4 flex flex-col">
                @for (s of [
                  { label: t('fields.render'), value: formatInterval(ver.renderInterval) },
                  { label: t('fields.connection'), value: formatInterval(ver.connectionInterval) },
                  { label: t('columns.powerMode'), value: ver.powerMode },
                  { label: t('thresholdLow'), value: ver.lowPowerPercentage + '% / ' + ver.lowPowerPercentageOut + '%' },
                  { label: t('thresholdNo'), value: ver.noPowerPercentage + '% / ' + ver.noPowerPercentageOut + '%' },
                  { label: t('wakeup'), value: ver.allowWakeup ? t('wakeupAllowed') : t('wakeupBlocked') }
                ]; track s.label) {
                  <div class="flex items-center justify-between gap-4 border-b border-hairline py-2.5">
                    <span class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">{{ s.label }}</span>
                    <span class="font-num text-[13px] font-semibold text-ink-1">{{ s.value }}</span>
                  </div>
                }
              </div>

              @if (ver.operations.length === 0) {
                <div class="text-[13px] text-ink-3">{{ t('history.noOperations') }}</div>
              } @else {
                <div class="flex flex-col gap-2">
                  @for (op of ver.operations; track op.operationId) {
                    <div class="flex flex-col rounded-control bg-sunken px-3 py-2">
                      <span class="font-num text-[13px] font-semibold text-ink-1">{{ op.operationId }}</span>
                      <span class="text-[12px] text-ink-2">{{ operationScheduleLabel(op) }}</span>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </ng-template>
    </app-grid>
  </app-grid-page>

  @if (isProfileDialogOpen()) {
    <app-profile-dialog
      [profile]="editingTemplate()"
      [mode]="dialogMode()"
      [busy]="isProfileSaving()"
      (save)="handleProfileSave($event)"
      (close)="closeProfileDialog()"
    />
  }

  @if (isOperationDialogOpen() && selectedTemplate()) {
    <app-operation-dialog
      [operation]="editingOperation()"
      [mode]="dialogMode()"
      [profileId]="selectedTemplate()!.id"
      (save)="handleOperationSave($event)"
      (close)="closeOperationDialog()"
    />
  }
</ng-container>
